import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ENUMBUILDINGTYPE_OPTION,
  EnumBuildingType,
  EnumProductionType,
} from "../../../generated/enums";
import { Building, CreateBuilding } from "../../../generated/types";
import { VALIDATION_RULES, validateForm } from "../../../utils/formValidation";
import { FieldLayout } from "../../common/FieldFrame";
import { FormInput, FormSelect } from "../../common/FormLayout";

/**
 * Libellés des types de production, définis ici plutôt que repris du mapping généré : sa
 * clé `breeder` est déjà celle du type de compte « Éleveur », et rendrait « Éleveur » là où
 * il faut lire « Reproducteur ».
 */
export const PRODUCTION_OPTIONS: {
  value: EnumProductionType;
  label: string;
}[] = [
  // i18n.t("setup-production-broiler")
  { value: "broiler", label: "setup-production-broiler" },
  // i18n.t("setup-production-layer")
  { value: "layer", label: "setup-production-layer" },
  // i18n.t("setup-production-breeder")
  { value: "breeder", label: "setup-production-breeder" },
];

const BUILDING_RULES = {
  name: [VALIDATION_RULES.required(), VALIDATION_RULES.minLength(2)],
  inuav: [VALIDATION_RULES.required()],
  type: [VALIDATION_RULES.required()],
  production_type: [VALIDATION_RULES.required()],
};

/** Champs saisis par l'éleveur ; `type` et `production_type` valent "" tant que rien n'est choisi. */
export type BuildingFormValues = {
  name: string;
  inuav: string;
  type: string;
  production_type: string;
};

export const EMPTY_BUILDING_FORM: BuildingFormValues = {
  name: "",
  inuav: "",
  type: "",
  production_type: "",
};

/** Valeurs non demandées à l'éleveur : forme et dimensions ne sont pas encore saisies dans le front. */
const BUILDING_DEFAULTS = {
  shape: "triangle_roof" as const,
  length: 0,
  width: 0,
  roof_height: 0,
  wall_height: 0,
  daily_measure_enabled: true,
};

// Les valeurs sont validées (BUILDING_RULES) avant l'appel : les selects ne proposent que
// des valeurs de l'énum.
const formToBuildingFields = (form: BuildingFormValues) => ({
  name: form.name,
  inuav: form.inuav,
  type: form.type as EnumBuildingType,
  production_type: form.production_type as EnumProductionType,
});

/** Nouveau bâtiment, sans produit : ils s'y rattachent ensuite. */
export const toCreateBuilding = (
  form: BuildingFormValues,
  breedingId: number
): CreateBuilding => ({
  ...BUILDING_DEFAULTS,
  ...formToBuildingFields(form),
  device: [],
  id_breeding: breedingId,
});

/** Bâtiment modifié : seuls les champs du formulaire changent, le reste est renvoyé tel quel. */
export const toUpdatedBuilding = (
  building: Building,
  form: BuildingFormValues
): Building => ({ ...building, ...formToBuildingFields(form) });

export const buildingToForm = (building: Building): BuildingFormValues => ({
  name: building.name,
  inuav: building.inuav,
  type: building.type,
  production_type: building.production_type,
});

/** État et validation du formulaire de bâtiment, partagés par la mise en route et la fiche élevage. */
export const useBuildingForm = (initial: BuildingFormValues) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const change = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const { [name]: _removed, ...rest } = prev;
      return rest;
    });
  };

  /** Vrai si le formulaire est valide ; sinon affiche les erreurs. */
  const validate = (): boolean => {
    const found = validateForm(form, BUILDING_RULES);
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  // Stable : un parent peut le rappeler dans un effet (réouverture d'une modale).
  const reset = useCallback((values: BuildingFormValues) => {
    setForm(values);
    setErrors({});
  }, []);

  return { form, errors, change, validate, reset };
};

export const BuildingFields = ({
  buildingForm,
  layout = "stacked",
}: {
  buildingForm: ReturnType<typeof useBuildingForm>;
  layout?: FieldLayout;
}) => {
  const { t } = useTranslation();
  const { form: values, errors, change: onChange } = buildingForm;
  return (
    <>
      <FormInput
        layout={layout}
        label={t("building-name")}
        name="name"
        value={values.name}
        onChange={onChange}
        required
        error={errors.name}
      />
      <FormInput
        layout={layout}
        label={t("building-inuav")}
        name="inuav"
        value={values.inuav}
        onChange={onChange}
        required
        error={errors.inuav}
      />
      <FormSelect
        layout={layout}
        label={t("building-type")}
        name="type"
        value={values.type}
        onChange={onChange}
        options={ENUMBUILDINGTYPE_OPTION}
        required
        error={errors.type}
      />
      <FormSelect
        layout={layout}
        label={t("building-production-type")}
        name="production_type"
        value={values.production_type}
        onChange={onChange}
        options={PRODUCTION_OPTIONS}
        required
        error={errors.production_type}
      />
    </>
  );
};
